import { useEffect, useMemo, useRef, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { v4 as uuid } from 'uuid';

import { convertWeiToEth } from '@pkg/utils';

import { type ActivityListEntryType, ActivityStatusTypes, type EventParsedData } from '@app/client';
import {
    callGetOwnersActivityListings,
    callGetOwnersActivitySales,
} from '@app/client/resources/collections/collection';
import { callGetTokenDetailsList } from '@app/client/resources/marketplace';
import { checkValidContractAddress } from '@app/helpers';

const COLLECTION_PAST_ACTIVITY_CACHE = 'collection-past-activity_cache';
export const useGetCollectionOwnersPastActivity = (
    slug?: string,
    collectionOwners?: string[],
    lastHours = 48,
    disabled = false,
) => {
    const [formattedData, setFormattedData] = useState<EventParsedData[]>([]);
    const [tokenAddresses, setTokenAddresses] = useState<string[]>([]);
    const [tokens, setTokens] = useState<{ contract: string; token_ids: string[] }[]>([]);
    const cache = [COLLECTION_PAST_ACTIVITY_CACHE, slug];
    const currSlug = useRef(slug);

    useEffect(() => {
        if (currSlug.current !== slug && !disabled) {
            currSlug.current = slug;
            setFormattedData([]);
            setTokens([]);
            setTokenAddresses([]);
        }
    }, [disabled, slug]);

    const salesResponse = useQuery(
        cache,
        ({ signal }) =>
            callGetOwnersActivitySales({
                slug,
                query: {
                    lastHours,
                    limit: 200,
                },
                settings: { signal },
            }),
        {
            retry: false,
            enabled: Boolean(slug) && !disabled,
        },
    );
    const listingsResponse = useQuery(
        cache,
        ({ signal }) =>
            callGetOwnersActivityListings({
                slug,
                query: {
                    lastHours,
                    limit: 200,
                },
                settings: { signal },
            }),
        {
            retry: false,
            enabled: Boolean(slug) && !disabled,
        },
    );

    useEffect(() => {
        salesResponse.data?.forEach(async (data) => {
            setFormattedData((curr) => [
                ...curr,
                {
                    contract_address: data.contract_address,
                    status: ActivityStatusTypes.Sale,
                    date: data.block_timestamp,
                    addressTo: data.buyer_address,
                    addressFrom: data.seller_address,
                    id: data.token_id.toString(),
                    value: convertWeiToEth(parseFloat(data.total_amount_wei.toString() || '0'), 2),
                    wallet_address: data.seller_address,
                },
            ]);
        });
        if (listingsResponse?.data?.ok && listingsResponse.data.value) {
            listingsResponse.data?.value?.forEach(async (data) => {
                setFormattedData((curr) => [
                    ...curr,
                    {
                        contract_address: data.contract_address,
                        status: ActivityStatusTypes.Listed,
                        date: data.block_timestamp,
                        addressTo: data.lister,
                        addressFrom: data.lister,
                        id: data.token_id.toString(),
                        value: convertWeiToEth(parseFloat(data.total_amount_wei.toString() || '0'), 2),
                        wallet_address: data.lister,
                    },
                ]);
            });
        }
    }, [listingsResponse.data, salesResponse.data]);

    useEffect(() => {
        const tokenDetailsProps = formattedData.reduce<{ [key: string]: string[] }>((acc, curr) => {
            const { contract_address, id } = curr;
            if (contract_address && id) {
                if (!acc[contract_address]) acc[contract_address] = [];
                acc[contract_address].push(id);
                setTokenAddresses((currentAddresses) => [
                    ...currentAddresses,
                    `${checkValidContractAddress(contract_address)}:${id}`,
                ]);
            }
            return acc;
        }, {});

        setTokens(
            Object.keys(tokenDetailsProps).map((contractAddress) => ({
                contract: checkValidContractAddress(contractAddress),
                token_ids: tokenDetailsProps[contractAddress],
            })),
        );
    }, [formattedData]);

    useEffect(() => {
        if (collectionOwners)
            setFormattedData((data) =>
                data.map((event) => {
                    if (event.status === ActivityStatusTypes.Sale && collectionOwners?.includes(event.addressTo)) {
                        event.status = ActivityStatusTypes.Buy;
                    }
                    return event;
                }),
            );
    }, [collectionOwners]);

    const { data: tokensData } = useQuery(
        ['pastActivityTokens', tokenAddresses],
        ({ signal }) => callGetTokenDetailsList({ tokens, settings: { signal } }),
        {
            keepPreviousData: true,
            retry: false,
            enabled: tokenAddresses.length > 0,
        },
    );

    const activityData = useMemo(
        () =>
            tokensData?.ok && tokensData?.value
                ? formattedData.map((eventData) => {
                      const tokenData = tokensData.value?.contents?.find(
                          (token) => token.collection === eventData.contract_address && token.token_id === eventData.id,
                      );
                      return {
                          ...eventData,
                          // UUIDs are used to identify event keys as they might not have unique data attached to them
                          uuid: uuid(),
                          image: tokenData?.image_url || '',
                          name: tokenData?.name || eventData.id,
                          value: eventData?.value || 0,
                          owner_yat: '',
                          twitter_handle: '',
                          wallet_address:
                              eventData.status === ActivityStatusTypes.Buy
                                  ? eventData.addressTo
                                  : eventData.addressFrom,
                      };
                  })
                : [],
        [formattedData, tokensData],
    );

    return {
        salesResponse,
        listingsResponse,
        data: useMemo(
            () =>
                activityData.sort((a, b) => {
                    if (a.date > b.date) {
                        return -1;
                    }
                    if (a.date < b.date) {
                        return 1;
                    }
                    return 0;
                }),
            [activityData],
        ) as ActivityListEntryType[],
    };
};
